import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import IconClock from '../icons/IconClock';

const QUICK_TIMES = (() => {
   const list = [];
   for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
         list.push(String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0'));
      }
   }
   return list;
})();

// Drop-in replacement for a plain <input type="time"> - same value contract
// ('HH:mm' 24h string, '' for unset). Typing is still free-form (any exact
// minute, not just the quarter-hour quick-picks below - lesson times aren't
// always round numbers) with light auto-formatting (digits -> "HH:mm",
// clamped on blur/Enter); the dropdown is a fast point-and-click shortcut
// for the common case, not the only way in. Portaled to document.body and
// positioned off the trigger's live rect - see DateInput.jsx for why
// (backdrop-filter stacking contexts + narrow host widgets both clip a
// plain position:absolute popup).
export default function TimeInput({ value, onChange, placeholder, disabled, className, style, align = 'left', icon = true }) {
   const [open, setOpen] = useState(false);
   const [text, setText] = useState(value || '');
   const [menuRect, setMenuRect] = useState(null);
   const rootRef = useRef(null);
   const inputRef = useRef(null);
   const menuRef = useRef(null);
   const activeItemRef = useRef(null);

   useEffect(() => {
      setText(value || '');
   }, [value]);

   // flips the list to open upward when there isn't enough room below - same
   // openUp technique as PlannedEventDetailModal.jsx's TimeOptionSelect and
   // DateInput.jsx's calendar. Matters whenever this is triggered from
   // something docked near the bottom of the viewport (e.g. the floating
   // chat popup), where "below the trigger" is often off-screen entirely.
   const MENU_HEIGHT_ESTIMATE = 220;
   const updateMenuRect = () => {
      if (!rootRef.current) return;
      const r = rootRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const openUp = spaceBelow < MENU_HEIGHT_ESTIMATE && r.top > spaceBelow;
      const vertical = openUp
         ? { bottom: window.innerHeight - r.top + 4 }
         : { top: r.bottom + 4 };
      setMenuRect(align === 'right'
         ? { ...vertical, right: window.innerWidth - r.right, width: Math.max(r.width, 90) }
         : { ...vertical, left: r.left, width: Math.max(r.width, 90) });
   };

   useLayoutEffect(() => {
      if (!open) return;
      updateMenuRect();
      const onReposition = () => updateMenuRect();
      window.addEventListener('scroll', onReposition, true);
      window.addEventListener('resize', onReposition);
      return () => {
         window.removeEventListener('scroll', onReposition, true);
         window.removeEventListener('resize', onReposition);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [open]);

   useEffect(() => {
      // menuRect (not just open) in the deps: the portal only actually
      // renders (and attaches activeItemRef) once menuRect is set by the
      // layout effect above, which happens on the render AFTER open first
      // flips true - an effect keyed on [open] alone would fire one render
      // too early, while the ref is still null, and never re-run since
      // open itself doesn't change again.
      if (open && menuRect && activeItemRef.current) {
         activeItemRef.current.scrollIntoView({ block: 'center' });
      }
   }, [open, menuRect]);

   useEffect(() => {
      const onClickOutside = (e) => {
         const inRoot = rootRef.current && rootRef.current.contains(e.target);
         const inMenu = menuRef.current && menuRef.current.contains(e.target);
         if (!inRoot && !inMenu) { setOpen(false); commit(); }
      };
      const onKeyDown = (e) => { if (e.key === 'Escape') { setText(value || ''); setOpen(false); } };
      document.addEventListener('mousedown', onClickOutside);
      document.addEventListener('keydown', onKeyDown);
      return () => {
         document.removeEventListener('mousedown', onClickOutside);
         document.removeEventListener('keydown', onKeyDown);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [text, value]);

   const formatDigits = (digits) => (digits.length <= 2 ? digits : digits.slice(0, 2) + ':' + digits.slice(2, 4));

   const handleTextChange = (raw) => {
      const digits = raw.replace(/\D/g, '').slice(0, 4);
      setText(formatDigits(digits));
   };

   // Reads `text` straight from this render's closure rather than via a
   // setText functional updater - commit() only ever runs from an event
   // handler (Enter, the document mousedown listener) after that render
   // has already committed, so the closure value is current. Calling
   // onChange (another component's setState) from inside a setText
   // updater instead triggers React's "Cannot update a component while
   // rendering a different component" warning, since updaters can run
   // during the render/reconciliation phase.
   const commit = () => {
      const digits = text.replace(/\D/g, '');
      if (digits.length === 0) {
         setText('');
         if (value !== '') onChange('');
         return;
      }
      const hh = Math.min(23, Math.max(0, parseInt(digits.slice(0, 2) || '0', 10)));
      const mm = digits.length > 2 ? Math.min(59, Math.max(0, parseInt(digits.slice(2, 4).padEnd(2, '0'), 10))) : 0;
      const normalized = String(hh).padStart(2, '0') + ':' + String(mm).padStart(2, '0');
      setText(normalized);
      if (normalized !== value) onChange(normalized);
   };

   const openDropdown = () => {
      if (disabled) return;
      setOpen(true);
   };

   const choose = (time) => {
      // deliberately doesn't blur the input afterward - commit() (wired to
      // onBlur) would re-run against `text` from this render's now-stale
      // closure (setText above is async) and could clobber the just-picked
      // value right back to whatever was typed before it.
      setText(time);
      if (time !== value) onChange(time);
      setOpen(false);
   };

   const handleKeyDown = (e) => {
      if (e.key === 'Enter') { commit(); setOpen(false); e.preventDefault(); }
      else if (e.key === 'ArrowDown' && !open) { openDropdown(); e.preventDefault(); }
   };

   const menu = open && menuRect ? createPortal(
      <div className='time-input-dropdown' ref={menuRef} style={{ position: 'fixed', ...menuRect }}>
         {QUICK_TIMES.map(time => (
            <div key={time} ref={time === value ? activeItemRef : null}
               className={'time-input-option' + (time === value ? ' selected' : '')}
               onMouseDown={(e) => { e.preventDefault(); choose(time); }}>
               {time}
            </div>
         ))}
      </div>,
      document.body
   ) : null;

   return (
      <div className={'time-input' + (className ? ' ' + className : '')} style={style} ref={rootRef}>
         {icon ? <span className='time-input-icon'><IconClock style={{ fill: 'currentColor' }} /></span> : ''}
         <input ref={inputRef} type='text' className='time-input-field' disabled={disabled}
            placeholder={placeholder} value={text} maxLength={5}
            onFocus={openDropdown} onClick={openDropdown}
            onChange={(e) => handleTextChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => { commit(); setOpen(false); }} />
         {menu}
      </div>
   );
}
